import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { H5, View, Text, Card, YStack, XStack, Button, H6, ScrollView } from 'tamagui';
import { getFlaggedItems, unflagItem } from '@api/api';
import { SectionList, ActivityIndicator, Alert, Pressable, Platform } from 'react-native';
import useGlobalUser from "@hooks/getGlobalUser";
import { MaterialIcons, MaterialCommunityIcons, Feather } from '@expo/vector-icons';
import { useTranslation } from "react-i18next";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import navigate from "../../../../rootNavigation";
import { Image } from 'expo-image';
import moment from 'moment';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '../../../../../../stylesheet';
import { Loading } from '@components/Global/Loading';

const FlaggedWidget = () => {
  const { t } = useTranslation();
  const globalId = useGlobalUser();
  const theme = themeSelector();
  const { userData, companyData } = useSelector((state: DefaultRootState) => state.promiseData?.data);
  const focusMode = useSelector((state: DefaultRootState) => state.UI?.focusMode);

  const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
    queryKey: ['flaggedItems', globalId, companyData?.companyNumber],
    queryFn: getFlaggedItems,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnMount: true
  });

  const handleUnflagItem = async (contentId: string) => {
    try {
      await unflagItem(contentId);
      refetch();
      Alert.alert('Success', 'Item unflagged successfully');
    } catch (error) {
      Alert.alert('Error', 'Failed to unflag item');
    }
  };

  // Compact Product Component (based on DefaultProduct)
  const CompactProduct = ({ item }: { item: any }) => {
    const productTitle = item.data?.product_name || item.data?.name || 'Product';
    const productPrice = item.data?.price;

    // Try multiple image sources
    const productImage = item.dataImages?.[0] ||
      item.data?.dataImages?.[0] ||
      item.data?.images?.[0] ||
      item.data?.image ||
      item.image;

    // Extract key product details like in DefaultProduct
    const getProductDetails = () => {
      const details = [];
      if (item.data?.product_registration_date) {
        details.push(['Registration Date', item.data.product_registration_date]);
      }
      if (item.data?.description) {
        details.push(['Description', item.data.description]);
      }
      return details.slice(0, 2); // Show max 2 details
    };

    const productDetails = getProductDetails();

    return (
      <Pressable
        onPress={() => {
          navigate('TimeLineData', {
            dataId: item._id,
            params: { data: item, schema: [] }
          });
        }}
        style={({ pressed }) => [
          getModernCardStyle(theme, 'medium'),
          pressed && { transform: [{ scale: 0.98 }] },
          { alignSelf: 'stretch' }
        ]}
      >
        <View style={styles.modernColumn}>
          <XStack space={12} alignItems="flex-start" flexShrink={1}>
            {/* Inline Product Image */}
            <View style={[styles.modernImageContainer, { width: 80, height: 80, flexShrink: 0 }]}>
              {productImage ? (
                <Image
                  source={{ uri: productImage }}
                  style={{ width: '100%', height: '100%' }}
                  contentFit="cover"
                />
              ) : (
                <View style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 12
                }}>
                  <MaterialIcons name="inventory" size={32} color="#3b82f6" />
                </View>
              )}
            </View>

            {/* Product Info */}
            <View style={[styles.modernColumn, { flex: 1, minWidth: 0 }]}>
              <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={8} flexShrink={1}>
                <View style={{ flex: 1, marginRight: 8, minWidth: 0 }}>
                  <Text
                    style={[
                      styles.modernCardTitle,
                      { color: getModernTextColor(theme, 'primary') }
                    ]}
                    numberOfLines={2}
                  >
                    {productTitle}
                  </Text>
                  {productPrice && (
                    <Text style={[styles.modernFieldValue, { color: '#3b82f6', fontSize: 14, fontWeight: '600', marginTop: 2 }]}>
                      £{productPrice}
                    </Text>
                  )}
                </View>
                <Pressable
                  onPress={() => handleUnflagItem(item._id)}
                  style={[
                    styles.modernActionButton,
                    getModernActionStyle(theme, 'red'),
                    { paddingHorizontal: 8, paddingVertical: 4, flexShrink: 0 }
                  ]}
                >
                  <MaterialIcons name="flag" size={16} color="#f56565" />
                </Pressable>
              </XStack>

              {/* Product Details */}
              {productDetails.map(([key, value], index) => (
                <View key={index} style={[styles.modernInfoRow, { marginBottom: 4 }]}>
                  <Text style={[styles.modernFieldLabel, { color: getModernTextColor(theme, 'secondary'), fontSize: 11 }]}>
                    {key}
                  </Text>
                  <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'primary'), fontSize: 12 }]} numberOfLines={1}>
                    {value}
                  </Text>
                </View>
              ))}

              <XStack space={8} alignItems="center" marginTop={8} flexShrink={1}>
                <MaterialCommunityIcons name="tag" size={14} color={getModernTextColor(theme, 'secondary')} style={{ flexShrink: 0 }} />
                <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'secondary'), fontSize: 11 }]} numberOfLines={1}>
                  {item.section || 'Product'}
                </Text>
              </XStack>
            </View>
          </XStack>
        </View>
      </Pressable>
    );
  };

  // Compact Customer Component (based on DefaultCustomer)
  const CompactCustomer = ({ item }: { item: any }) => {
    const customerName = `${item.data?.name || ''} ${item.data?.surname || ''}`.trim() || 'Customer';
    const customerEmail = item.data?.email;
    const customerPhone = item.data?.phone;
    const customerImage = item.image;
    const customerLocation = item.data?.location;

    // Extract key customer details like in DefaultCustomer
    const getCustomerDetails = () => {
      const details = [];
      if (customerEmail) {
        details.push(['Email', customerEmail]);
      }
      if (customerPhone) {
        details.push(['Phone', customerPhone]);
      }
      if (customerLocation) {
        details.push(['Location', customerLocation]);
      }
      return details.slice(0, 2); // Show max 2 details
    };

    const customerDetails = getCustomerDetails();

    return (
      <Pressable
        onPress={() => {
          navigate('TimeLineData', {
            dataId: item._id,
            params: { data: item, schema: [] }
          });
        }}
        style={({ pressed }) => [
          getModernCardStyle(theme, 'medium'),
          pressed && { transform: [{ scale: 0.98 }] },
          { alignSelf: 'stretch' }
        ]}
      >
        <View style={styles.modernColumn}>
          <XStack space={12} alignItems="center" marginBottom={12} flexShrink={1}>
            {/* Customer Avatar */}
            <View style={[styles.modernAvatarContainer, { width: 60, height: 60, flexShrink: 0 }]}>
              {customerImage ? (
                <Image
                  source={{ uri: customerImage }}
                  style={{ width: 60, height: 60, borderRadius: 20 }}
                  contentFit="cover"
                />
              ) : (
                <MaterialIcons name="person" size={28} color="#3b82f6" />
              )}
            </View>

            {/* Customer Info */}
            <View style={[styles.modernColumn, { flex: 1, minWidth: 0 }]}>
              <Text
                style={[
                  styles.modernCardTitle,
                  { color: getModernTextColor(theme, 'primary'), marginBottom: 4 }
                ]}
                numberOfLines={1}
              >
                {customerName}
              </Text>
              {customerDetails.map(([key, value], index) => (
                <Text
                  key={index}
                  style={[
                    styles.modernFieldValue,
                    { color: getModernTextColor(theme, 'secondary'), fontSize: 12 }
                  ]}
                  numberOfLines={1}
                >
                  {value}
                </Text>
              ))}
            </View>

            <Pressable
              onPress={() => handleUnflagItem(item._id)}
              style={[
                styles.modernActionButton,
                getModernActionStyle(theme, 'red'),
                { paddingHorizontal: 8, paddingVertical: 4, flexShrink: 0 }
              ]}
            >
              <MaterialIcons name="flag" size={16} color="#f56565" />
            </Pressable>
          </XStack>

          {/* Quick Action Buttons */}
          <XStack space={8} alignItems="center" flexShrink={1}>
            {customerPhone && (
              <Pressable
                style={[
                  styles.modernActionButton,
                  getModernActionStyle(theme, 'blue'),
                  { width: 36, height: 36, borderRadius: 18, flexShrink: 0 }
                ]}
                onPress={() => {
                  // Handle phone call
                  console.log('Call customer:', customerPhone);
                }}
              >
                <MaterialIcons name="phone" size={18} color="#3b82f6" />
              </Pressable>
            )}

            {customerEmail && (
              <Pressable
                style={[
                  styles.modernActionButton,
                  getModernActionStyle(theme, 'red'),
                  { width: 36, height: 36, borderRadius: 18, flexShrink: 0 }
                ]}
                onPress={() => {
                  // Handle email
                  console.log('Email customer:', customerEmail);
                }}
              >
                <MaterialIcons name="email" size={18} color="#f56565" />
              </Pressable>
            )}

            <XStack space={4} alignItems="center" flex={1} minWidth={0}>
              <MaterialCommunityIcons name="tag" size={16} color={getModernTextColor(theme, 'secondary')} style={{ flexShrink: 0 }} />
              <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'secondary'), fontSize: 12 }]} numberOfLines={1}>
                {item.section || 'Customer'}
              </Text>
            </XStack>
          </XStack>
        </View>
      </Pressable>
    );
  };

  const renderFlaggedItem = ({ item }: { item: any }) => {
    // Use compact components for specific types
    if (item.section === 'product') {
      return <CompactProduct item={item} />;
    }

    if (item.section === 'customer') {
      return <CompactCustomer item={item} />;
    }

    // Default compact view for other types
    const getItemTitle = (item: any) => {
      if (item.data) {
        return item.data.name ||
          item.data.product_name ||
          item.data.title ||
          item.data.first_field ||
          `${item.data.name || ''} ${item.data.surname || ''}`.trim() ||
          'Untitled Item';
      }
      return item.title || item.name || 'Untitled Item';
    };

    const getItemDescription = (item: any) => {
      if (item.data) {
        return item.data.notes ||
          item.data.description ||
          item.data.email ||
          item.data.phone ||
          null;
      }
      return item.description;
    };

    return (
      <Pressable
        onPress={() => {
          navigate('TimeLineData', {
            dataId: item._id,
            params: { data: item, schema: [] }
          });
        }}
        style={({ pressed }) => [
          getModernCardStyle(theme, 'medium'),
          pressed && { transform: [{ scale: 0.98 }] },
          { alignSelf: 'stretch' }
        ]}
      >
        <View style={styles.modernColumn}>
          <XStack justifyContent="space-between" alignItems="center" marginBottom={8} flexShrink={1}>
            <Text
              style={[
                styles.modernCardTitle,
                { color: getModernTextColor(theme, 'primary'), flex: 1, marginRight: 8, minWidth: 0 }
              ]}
              numberOfLines={1}
            >
              {getItemTitle(item)}
            </Text>
            <Pressable
              onPress={() => handleUnflagItem(item._id)}
              style={[
                styles.modernActionButton,
                getModernActionStyle(theme, 'red'),
                { paddingHorizontal: 8, paddingVertical: 4, flexShrink: 0 }
              ]}
            >
              <MaterialIcons name="flag" size={16} color="#f56565" />
            </Pressable>
          </XStack>

          {getItemDescription(item) && (
            <View style={styles.modernInfoRow}>
              <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'primary') }]} numberOfLines={2}>
                {getItemDescription(item)}
              </Text>
            </View>
          )}

          <XStack space={8} alignItems="center" marginTop={8} flexShrink={1}>
            <MaterialCommunityIcons name="tag" size={16} color={getModernTextColor(theme, 'secondary')} style={{ flexShrink: 0 }} />
            <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'secondary'), fontSize: 12 }]} numberOfLines={1}>
              {item.section || 'General'}
            </Text>
          </XStack>

          <XStack space={8} alignItems="center" marginTop={4} flexShrink={1}>
            <MaterialCommunityIcons name="calendar" size={16} color={getModernTextColor(theme, 'secondary')} style={{ flexShrink: 0 }} />
            <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'secondary'), fontSize: 12 }]} numberOfLines={1}>
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          </XStack>
        </View>
      </Pressable>
    );
  };

  const renderSectionHeader = ({ section }: { section: any }) => (
    <View style={[
      styles.modernSection,
      {
        paddingVertical: 16,
        paddingHorizontal: 20,
        marginBottom: 8,
        backgroundColor: theme ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.1)',
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#3b82f6',
        marginTop: 8,
        shadowColor: '#3b82f6',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 2,
        alignSelf: 'stretch'
      }
    ]}>
      <XStack alignItems="center" space={8} flexShrink={1}>
        <View style={[
          styles.modernActionButton,
          getModernActionStyle(theme, 'blue'),
          { width: 32, height: 32, borderRadius: 16, padding: 0, flexShrink: 0 }
        ]}>
          <MaterialIcons name="flag" size={18} color="#3b82f6" />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={[
              styles.modernSectionTitle,
              {
                color: getModernTextColor(theme, 'primary'),
                fontSize: 16,
                fontWeight: '700',
                letterSpacing: 0.5
              }
            ]}
            numberOfLines={1}
          >
            {(section.title || 'Flagged Items').replace(/\b\w/g, l => l.toUpperCase())}
          </Text>
          <Text style={[
            styles.modernFieldValue,
            {
              color: getModernTextColor(theme, 'secondary'),
              fontSize: 12,
              marginTop: 2,
              letterSpacing: 0.5,
              fontWeight: '500'
            }
          ]}>
            {section.data?.length || 0} {section.data?.length === 1 ? 'Item' : 'Items'}
          </Text>
        </View>
        <View style={[
          styles.modernActionButton,
          getModernActionStyle(theme, 'blue'),
          {
            width: 28,
            height: 28,
            borderRadius: 14,
            padding: 0,
            opacity: 0.7,
            flexShrink: 0
          }
        ]}>
          <MaterialIcons name="keyboard-arrow-down" size={16} color="#3b82f6" />
        </View>
      </XStack>
    </View>
  );

  if (isLoading || isFetching) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: "center", alignSelf: 'stretch' }}>
        <Loading />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[getModernCardStyle(theme, 'medium'), { alignItems: 'center', paddingVertical: 24, alignSelf: 'stretch' }]}>
        <MaterialIcons name="error-outline" size={48} color="red" />
        <Text style={[styles.modernCardTitle, { color: getModernTextColor(theme, 'primary'), textAlign: 'center', marginTop: 12, paddingHorizontal: 16 }]}>
          {t('Failed to load flagged items')}
        </Text>
        <Pressable
          onPress={() => refetch()}
          style={[
            styles.modernActionButton,
            getModernActionStyle(theme, 'blue'),
            { marginTop: 16, paddingHorizontal: 16, paddingVertical: 8 }
          ]}
        >
          <Text style={[styles.modernFieldValue, { color: 'white', fontWeight: '600' }]}>
            {t('Retry')}
          </Text>
        </Pressable>
      </View>
    );
  }

  // Ensure data is an array and handle different response structures
  const flaggedItems = Array.isArray(data) ? data : (data?.data || []);

  console.log('FlaggedWidget - Raw data:', data);
  console.log('FlaggedWidget - Processed flaggedItems:', flaggedItems);
  console.log('FlaggedWidget - FlaggedItems length:', flaggedItems.length);

  if (!flaggedItems || flaggedItems.length === 0) {
    return (
      <View style={[getModernCardStyle(theme, 'medium'), { alignItems: 'center', paddingVertical: 24, alignSelf: 'stretch' }]}>
        <MaterialIcons name="flag" size={48} color={getModernTextColor(theme, 'secondary')} />
        <Text style={[styles.modernCardTitle, { color: getModernTextColor(theme, 'primary'), textAlign: 'center', marginTop: 12, paddingHorizontal: 16 }]}>
          {t('No flagged items found')}
        </Text>
        <Text style={[styles.modernFieldValue, { color: getModernTextColor(theme, 'secondary'), textAlign: 'center', marginTop: 8, paddingHorizontal: 16 }]}>
          {t('Items you flag will appear here for review')}
        </Text>
      </View>
    );
  }

  // Group items by section if they have sections
  const groupedData = flaggedItems.reduce((acc: any[], item: any) => {
    const section = item.section || 'General';
    const existingSection = acc.find(s => s.title === section);

    if (existingSection) {
      existingSection.data.push(item);
    } else {
      acc.push({
        title: section,
        data: [item]
      });
    }

    return acc;
  }, []);

  return (
    <View left={40} maxWidth={"83%"} style={[getModernCardStyle(theme, 'large'), { flex: 1, alignSelf: 'stretch', paddingBottom: 150 }]}>
      <View style={styles.modernColumn}>
        <XStack justifyContent="space-between" alignItems="center" marginBottom={16} flexShrink={1}>
          <Text style={[styles.modernSectionTitle, { color: getModernTextColor(theme, 'primary'), flex: 1, minWidth: 0 }]}>
            {t('Flagged Items')} ({flaggedItems.length})
          </Text>
          <Pressable
            onPress={() => refetch()}
            style={[
              styles.modernActionButton,
              getModernActionStyle(theme, 'blue'),
              { paddingHorizontal: 12, paddingVertical: 8, flexShrink: 0 }
            ]}
          >
            <MaterialIcons name="refresh" size={16} color="#3b82f6" />
          </Pressable>
        </XStack>

        <SectionList
          sections={groupedData}
          keyExtractor={(item, index) => `${item._id || item.id || index}`}
          renderItem={renderFlaggedItem}
          renderSectionHeader={renderSectionHeader}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 20 }}
          style={{ alignSelf: 'stretch' }}
        />
      </View>
    </View>
  );
};

export default FlaggedWidget;